import claude from './claudeClient';

/**
 * Maps Anthropic API error status codes to user-friendly error messages.
 * @param {number} statusCode - The HTTP status code from Anthropic API.
 * @param {Object} errorData - The error data from API response.
 * @returns {Object} Object with isInternal flag and error message.
 */
function getErrorMessage(statusCode, errorData) {
  if (statusCode === 401) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your Anthropic API key.' };
  } else if (statusCode === 403) {
    return { isInternal: true, message: 'Permission denied. Your API key does not have access to the specified resource.' };
  } else if (statusCode === 404) {
    return { isInternal: true, message: 'Resource not found. The requested endpoint or model may not exist.' };
  } else if (statusCode === 429) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (statusCode === 500) {
    return { isInternal: true, message: 'Anthropic service error. An unexpected error occurred on their servers. Please try again later.' };
  } else if (statusCode === 529) {
    return { isInternal: true, message: 'Anthropic service is temporarily overloaded. Please try again in a few moments.' };
  } else {
    return { isInternal: false, message: errorData?.error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Generates Arabic-formatted legal contract using Anthropic Claude.
 * @param {Object} contractData - Contract details including type, parties, property, terms.
 * @param {Object} templateOptions - Template customization options.
 * @returns {Promise<Object>} Generated contract with Arabic text and metadata.
 */
export async function generateArabicContract(contractData, templateOptions = {}) {
  try {
    const {
      contractType = 'rental',
      partyA,
      partyB,
      propertyDetails,
      clientDetails,
      duration,
      amount,
      terms = '',
      startDate,
      endDate
    } = contractData;

    const {
      includeWitnesses = true,
      includeNotarization = true,
      includePaymentSchedule = true,
      includeMaintenanceClause = true,
      includeTerminationClause = true,
      customClauses = [],
      includeLogo = true,
      includeWatermark = false
    } = templateOptions;

    // Get company branding for logo inclusion
    let companyBranding = null;
    if (includeLogo || includeWatermark) {
      try {
        const { getCompanyBranding } = await import('./companySettingsService');
        companyBranding = await getCompanyBranding();
      } catch (error) {
        console.warn('Could not load company branding:', error);
      }
    }

    const contractTypeArabic = {
      rental: 'إيجار',
      sale: 'بيع',
      partnership: 'شراكة',
      management: 'إدارة'
    }?.[contractType] || 'إيجار';

    const prompt = `أنت خبير قانوني متخصص في صياغة العقود العقارية باللغة العربية وفقاً للقوانين الأردنية.

قم بإنشاء عقد ${contractTypeArabic} عقاري كامل ومفصل باللغة العربية الفصحى بصيغة قانونية احترافية.

${includeLogo && companyBranding ? `**معلومات الشركة:**
- اسم الشركة: ${companyBranding?.companyName}
` : ''}

**معلومات العقد:**
- نوع العقد: ${contractTypeArabic}
- الطرف الأول (${contractType === 'rental' ? 'المؤجر' : contractType === 'sale' ? 'البائع' : 'الطرف الأول'}): ${partyA || '[اسم الطرف الأول]'}
- الطرف الثاني (${contractType === 'rental' ? 'المستأجر' : contractType === 'sale' ? 'المشتري' : 'الطرف الثاني'}): ${partyB || clientDetails?.name || '[اسم الطرف الثاني]'}
${clientDetails?.phone ? `- هاتف الطرف الثاني: ${clientDetails?.phone}` : ''}
${clientDetails?.email ? `- بريد الطرف الثاني: ${clientDetails?.email}` : ''}

**معلومات العقار:**
${propertyDetails?.title ? `- اسم العقار: ${propertyDetails?.title}` : ''}
${propertyDetails?.type ? `- نوع العقار: ${propertyDetails?.type}` : ''}
${propertyDetails?.address ? `- العنوان: ${propertyDetails?.address}` : ''}
${propertyDetails?.city ? `- المدينة: ${propertyDetails?.city}` : ''}
${propertyDetails?.district ? `- المنطقة: ${propertyDetails?.district}` : ''}
${propertyDetails?.area ? `- المساحة: ${propertyDetails?.area} متر مربع` : ''}
${propertyDetails?.bedrooms ? `- عدد الغرف: ${propertyDetails?.bedrooms}` : ''}
${propertyDetails?.bathrooms ? `- عدد الحمامات: ${propertyDetails?.bathrooms}` : ''}
${propertyDetails?.floor ? `- الطابق: ${propertyDetails?.floor}` : ''}
${propertyDetails?.features?.length ? `- المميزات: ${propertyDetails?.features?.join('، ')}` : ''}

**الشروط المالية:**
${amount ? `- ${contractType === 'rental' ? 'قيمة الإيجار' : 'قيمة البيع'}: ${amount} دينار أردني` : ''}
${duration ? `- مدة العقد: ${duration} شهر` : ''}
${startDate ? `- تاريخ البدء: ${startDate}` : ''}
${endDate ? `- تاريخ الانتهاء: ${endDate}` : ''}

**شروط إضافية:**
${terms || 'لا توجد شروط إضافية'}

**متطلبات الصياغة:**
1. استخدم اللغة العربية الفصحى القانونية الاحترافية
2. اتبع التنسيق القانوني الأردني المعتمد
3. ${includeWitnesses ? 'أضف قسم للشهود مع مساحات للتوقيع' : 'لا تضف قسم الشهود'}
4. ${includeNotarization ? 'أضف قسم التوثيق الرسمي' : 'لا تضف قسم التوثيق'}
5. ${includePaymentSchedule ? 'أضف جدول الدفعات المالية' : 'لا تضف جدول الدفعات'}
6. ${includeMaintenanceClause ? 'أضف بند الصيانة والمسؤوليات' : 'لا تضف بند الصيانة'}
7. ${includeTerminationClause ? 'أضف بند إنهاء العقد والشروط الجزائية' : 'لا تضف بند الإنهاء'}
${customClauses?.length ? `8. أضف البنود المخصصة التالية: ${customClauses?.join('، ')}` : ''}
9. رقم جميع البنود والفقرات بشكل واضح
10. أضف مساحات للتوقيعات والتواريخ في نهاية العقد

**الهيكل المطلوب:**
- ديباجة العقد
- بيانات الأطراف
- موضوع العقد ووصف العقار
- الشروط والأحكام (مرقمة)
- الالتزامات المالية
- حقوق وواجبات الطرفين
- شروط الإنهاء والتجديد
- التوقيعات والتواريخ

قم بإنشاء العقد الكامل بصيغة HTML منسقة مع استخدام علامات <div> و <p> و <h1> و <h2> و <h3> للتنسيق. استخدم dir="rtl" للنصوص العربية.`;

    const response = await claude?.messages?.create({
      model: 'claude-sonnet-4-5-20250929',
      max_tokens: 8192,
      messages: [
        {
          role: 'user',
          content: prompt
        }
      ]
    });

    const contractHTML = response?.content?.[0]?.text;

    if (!contractHTML) {
      throw new Error('Failed to generate contract content');
    }

    return {
      success: true,
      contractHTML,
      metadata: {
        contractType,
        partyA,
        partyB,
        propertyId: propertyDetails?.id,
        amount,
        duration,
        generatedAt: new Date()?.toISOString(),
        model: 'claude-sonnet-4-5-20250929'
      }
    };
  } catch (error) {
    if (error?.status) {
      const errorInfo = getErrorMessage(error?.status, error);
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', error);
      }
      throw new Error(errorInfo.message);
    }
    console.error('Error generating Arabic contract:', error);
    throw new Error(error?.message || 'Failed to generate Arabic contract');
  }
}

/**
 * Generates contract with streaming for real-time preview.
 * @param {Object} contractData - Contract details.
 * @param {Object} templateOptions - Template customization options.
 * @param {Function} onChunk - Callback for each text chunk.
 * @returns {Promise<Object>} Complete contract data.
 */
export async function generateArabicContractStreaming(contractData, templateOptions = {}, onChunk) {
  try {
    const {
      contractType = 'rental',
      partyA,
      partyB,
      propertyDetails,
      clientDetails,
      duration,
      amount,
      terms = '',
      startDate,
      endDate
    } = contractData;

    const {
      includeWitnesses = true,
      includeNotarization = true,
      includePaymentSchedule = true,
      includeMaintenanceClause = true,
      includeTerminationClause = true,
      customClauses = []
    } = templateOptions;

    const contractTypeArabic = {
      rental: 'إيجار',
      sale: 'بيع',
      partnership: 'شراكة',
      management: 'إدارة'
    }?.[contractType] || 'إيجار';

    const prompt = `أنت خبير قانوني متخصص في صياغة العقود العقارية باللغة العربية وفقاً للقوانين الأردنية.

قم بإنشاء عقد ${contractTypeArabic} عقاري كامل ومفصل باللغة العربية الفصحى بصيغة قانونية احترافية.

**معلومات العقد:**
- نوع العقد: ${contractTypeArabic}
- الطرف الأول: ${partyA || '[اسم الطرف الأول]'}
- الطرف الثاني: ${partyB || clientDetails?.name || '[اسم الطرف الثاني]'}
${propertyDetails?.title ? `- العقار: ${propertyDetails?.title}` : ''}
${amount ? `- المبلغ: ${amount} دينار أردني` : ''}
${duration ? `- المدة: ${duration} شهر` : ''}

قم بإنشاء العقد الكامل بصيغة HTML منسقة مع استخدام dir="rtl" للنصوص العربية.`;

    let fullContent = '';

    const stream = await claude?.messages?.stream({
      model: 'claude-sonnet-4-5-20250929',
      max_tokens: 8192,
      messages: [
        {
          role: 'user',
          content: prompt
        }
      ]
    });

    for await (const chunk of stream) {
      if (chunk?.type === 'content_block_delta' && chunk?.delta?.type === 'text_delta') {
        const text = chunk?.delta?.text;
        fullContent += text;
        if (onChunk) {
          onChunk(text);
        }
      }
    }

    return {
      success: true,
      contractHTML: fullContent,
      metadata: {
        contractType,
        partyA,
        partyB,
        propertyId: propertyDetails?.id,
        amount,
        duration,
        generatedAt: new Date()?.toISOString(),
        model: 'claude-sonnet-4-5-20250929'
      }
    };
  } catch (error) {
    if (error?.status) {
      const errorInfo = getErrorMessage(error?.status, error);
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', error);
      }
      throw new Error(errorInfo.message);
    }
    console.error('Error generating Arabic contract with streaming:', error);
    throw new Error(error?.message || 'Failed to generate Arabic contract');
  }
}

export default {
  generateArabicContract,
  generateArabicContractStreaming
};